import {
  GetPurchasesApiResponseData,
  ApiDocument,
  ApiPurchase,
  GetPurchasesResponseData
} from 'Services/apiResponseTypes'
import { Purchase, ReportedPurchase, ReportedPurchaseTableRow } from 'Utils/types/purchase'
import { CurrencyCode } from 'Utils/types/currencies'
import { PurchaseDocument } from 'Utils/types/purchaseDocument'
import { parseServerDate } from 'Utils/helpers/dateUtils'
import { ApiAdminTransaction, ApiReportedPurchase } from 'Services/JOONAdmin/adminApiResponseTypes'

export const parseDashboardResponseData = (
  data: GetPurchasesApiResponseData
): GetPurchasesResponseData => {
  const { purchases } = data

  return {
    purchases: purchases.map(parsePurchase)
  }
}

export const parsePurchase = (purchase: ApiPurchase): Purchase => {
  const {
    id,
    amount,
    transaction_date,
    name,
    plaid_category,
    reported,
    parent_id,
    benefit_program_name,
    benefit_program_id,
    benefit_program_transactions,
    opr_status,
    opr_request_eligible,
    manual,
    report_details,
    iso_currency_code,
    documents
  } = purchase

  // A purchase having a BPT with "approved_for_reimbursement" is what determines if it is approved
  const approved = benefit_program_transactions.some((t) => t.approved_for_reimbursement)

  return {
    id,
    amount,
    name,
    pending: reported && report_details?.status === 'REPORTED',
    approved,
    category: plaid_category,
    approvedProgramId: benefit_program_id,
    parentId: parent_id ?? undefined,
    approvedProgramName: benefit_program_name ?? report_details?.reported_program_name,
    reportedProgramName: report_details?.reported_program_name,
    date: parseServerDate(transaction_date),
    oprStatus: opr_status,
    source: manual ? 'Manually added' : 'Purchase account',
    dateReported: report_details ? parseServerDate(report_details.reported_at) : undefined,
    currencyCode: iso_currency_code,
    oprRequestEligible: opr_request_eligible ?? false,
    description: report_details?.description ?? undefined,
    documents:
      documents?.documents_list?.map((document) => {
        return parsePurchaseDocument(document)
      }) ?? []
  }
}

export const parsePurchaseDocument = (document: ApiDocument): PurchaseDocument => {
  const { id, original_filename, file_type, filename, urls } = document

  return {
    id,
    filename,
    urls,
    fileType: file_type,
    originalFilename: original_filename
  }
}

export const parseAdminTransaction = (txn: ApiAdminTransaction): ReportedPurchaseTableRow => ({
  id: txn.id,
  purchaseId: txn.id,
  userId: txn.employee_details.id,
  userEmail: txn.employee_details.email,
  employerName: txn.employee_details.employer_name ?? '',
  benefitProgramId: txn.benefit_program_id ?? undefined,
  programName: txn.benefit_program_name ?? '',
  name: txn.name,
  amount: txn.amount,
  date: parseServerDate(txn.transaction_date),
  dateReported: txn.report_details ? parseServerDate(txn.report_details.reported_at) : undefined,
  status: txn.report_details?.status,
  plaidCategory: txn.plaid_category ?? '',
  reportedCategory: txn.reported_category ?? undefined,
  documents:
    txn.documents?.documents_list?.map((document) => parsePurchaseDocument(document)) ?? [],
  description: txn.report_details?.description ?? '',
  currencyCode: txn.iso_currency_code ?? CurrencyCode.USDollar,
  oprStatus: txn.opr_status,
  approvedForReimbursement: false,
  categoryGuide: '',
  additionalReviewRequestedByMe: false,
  oprResettable: false
})

export const parseReportedPurchase = (reportedPurchase: ApiReportedPurchase): ReportedPurchase => {
  const {
    id,
    status,
    createdAt,
    updatedAt,
    description,
    benefitProgramId,
    benefitProgramName,
    benefitProgramCategoryGuide,
    user,
    txn,
    benefitProgramCategory,
    rejectionReason,
    additionalReviewRequestedById,
    additionalReviewRequestedByName,
    additionalReviewRequestedByMe,
    oprResettable
  } = reportedPurchase

  const {
    id: purchaseId,
    transaction_date,
    iso_currency_code,
    name,
    opr_status,
    amount_precise,
    approved_for_reimbursement,
    plaid_category,
    serialized_documents
  } = txn

  const { id: userId, email, employer } = user

  return {
    id,
    purchaseId,
    reportedTransactionId: id,
    userId,
    name,
    description,
    status,
    userEmail: email,
    programName: benefitProgramName,
    categoryGuide: benefitProgramCategoryGuide || '',
    amount: amount_precise,
    oprStatus: opr_status,
    currencyCode: iso_currency_code,
    approvedForReimbursement: approved_for_reimbursement,
    employerName: employer?.name,
    reportedCategory: benefitProgramCategory?.basic_benefit_program_category?.name,
    categoryTaxable: benefitProgramCategory?.taxable,
    plaidCategory: plaid_category?.name || '',
    benefitProgramId: parseInt(benefitProgramId),
    dateReported: parseServerDate(createdAt),
    dateUpdated: parseServerDate(updatedAt),
    date: parseServerDate(transaction_date),
    rejectionReason: rejectionReason,
    additionalReviewRequestedById: additionalReviewRequestedById ?? undefined,
    additionalReviewRequestedByName: additionalReviewRequestedByName ?? undefined,
    additionalReviewRequestedByMe: additionalReviewRequestedByMe ?? false,
    oprResettable: oprResettable ?? false,
    documents:
      serialized_documents?.documents_list?.map((document) => {
        return parsePurchaseDocument(document)
      }) ?? []
  }
}
